import { jsx as _jsx } from "react/jsx-runtime";
import { CxmEntityType, FilterType, getCxmFilmsForComponent, getFilms, getFilterByIdAndType, getMemberHash, isEmptyArray, loadCxmFilmsForComponent, loadFilmAvailabilities, loadFilms, LoadingState } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { useOccContext } from '../../context/occ/hooks/use-occ-context';
import { useMemberRewards } from '../../hooks/use-member-rewards';
import { sortAndSliceFilms } from '../../utils/film-sort-utils';
import { withComponentWrapper } from '../../utils/with-component-wrapper';
import { useFilmListMessageProps } from './hooks/use-film-list-message-props';
import { FilmListCarousel } from './subcomponents/carousel';
import { FilmListGrid } from './subcomponents/grid';
import { FilmListMessage, FilmListMessageType } from './subcomponents/message';
import { FilmListSkeleton } from './subcomponents/skeleton';
import { FilmListMode } from './types/config';
import { FilmListContextInstance, mapFilmListContext } from './types/context';
import { defaultFilmListProps, filmListPropsValidator } from './types/props';
import { setupFilter } from './utils/setup-filter';
const b = bemClassBuilder('v-film-list');
export const FilmList = observer(props => {
    const { i18n, config: { enableSkeletonLoadingIndicators } } = useOccContext();
    const { config, siteId } = props;
    const { filter: filterConfig, componentId, maximumFilms } = config;
    const [loadingState, setLoadingState] = useState(LoadingState.Loading);
    const memberHash = getMemberHash(); // indirect dependency, used by getRequestContext
    const filterId = filterConfig === null || filterConfig === void 0 ? void 0 : filterConfig.filterId;
    const filter = filterId ? getFilterByIdAndType(filterId, FilterType.Film) : undefined;
    useEffect(() => {
        (async () => {
            setLoadingState(LoadingState.Loading);
            try {
                await Promise.all([
                    loadFilms(siteId),
                    loadFilmAvailabilities(siteId),
                    loadCxmFilmsForComponent(CxmEntityType.FilmList, componentId, maximumFilms)
                ]);
                setupFilter(filterConfig, siteId);
                setLoadingState(LoadingState.Success);
            }
            catch (error) {
                setLoadingState(LoadingState.Failure);
            }
        })();
    }, [siteId, componentId, maximumFilms, memberHash, filterConfig]);
    // Load member rewards to show reward-restricted booking periods.
    useMemberRewards();
    const filmListContext = useMemo(() => mapFilmListContext(props), [props]);
    const messageProps = useFilmListMessageProps(config.filter);
    if (loadingState === LoadingState.Loading) {
        return enableSkeletonLoadingIndicators ? (_jsx(FilmListContextInstance.Provider, Object.assign({ value: filmListContext }, { children: _jsx(FilmListSkeleton, {}) }))) : null;
    }
    if (loadingState === LoadingState.Failure) {
        return (_jsx(FilmListContextInstance.Provider, Object.assign({ value: filmListContext }, { children: _jsx(FilmListMessage, { messageType: FilmListMessageType.FailedToLoadData }) })));
    }
    const films = getFilms(siteId, filter) || [];
    const cxmFilms = componentId ? getCxmFilmsForComponent(componentId) : undefined;
    const sortedFilms = sortAndSliceFilms(films, i18n, cxmFilms, maximumFilms);
    if (isEmptyArray(sortedFilms)) {
        return (_jsx(FilmListContextInstance.Provider, Object.assign({ value: filmListContext }, { children: _jsx(FilmListMessage, Object.assign({}, messageProps, { filterId: filterId })) })));
    }
    return (_jsx(FilmListContextInstance.Provider, Object.assign({ value: filmListContext }, { children: _jsx("div", Object.assign({ className: b.block() }, { children: config.mode === FilmListMode.Carousel ? (_jsx(FilmListCarousel, { films: sortedFilms })) : (_jsx(FilmListGrid, { films: sortedFilms })) })) })));
});
FilmList.displayName = 'FilmList';
/**
 * The **Film List** component renders a list of films.
 */
export const FilmListComponent = withComponentWrapper(FilmList, defaultFilmListProps, filmListPropsValidator);
